import { useContext, useEffect, useMemo } from 'react';

import { encodeCustomSpecialCharacters } from '@testsigma/organism';

import { UserPreferenceContext } from '@contexts/user-preference-context';
import { useSFConnectionState } from '@contexts/sf-connection-context';
import useFetchListData from '@hooks/use-fetch-list-data';
import { TestCase } from '@modules/test-development/models/test-case.model';
import TestCaseService from '@modules/test-development/services/test-case.service';
import { DeleteScreenModeType } from '../modals/delete-connection-modal';

interface UseFetchLinkedEntitiesProps {
  handleUpdateScreenMode(value: DeleteScreenModeType): void;
}

const getFormattedQuery = (queryParams: object) => {
  const formattedQueryParams = Object.entries(queryParams)
    .map(([key, value]) => `${key}:${value}`)
    .join(',');

  return formattedQueryParams;
};

const useFetchLinkedEntities = (props: UseFetchLinkedEntitiesProps) => {
  const { handleUpdateScreenMode } = props;
  const userPreferenceContext = useContext(UserPreferenceContext);
  const { selectedConnection, handleHideDeleteModal } = useSFConnectionState();

  const testCaseParams = useMemo(() => {
    const versionId =
      userPreferenceContext?.userPreference?.preference?.projectPreference
        .versionId;

    const queryParams = {
      applicationVersionId: `${versionId}`,
      afterTestParentId: null,
      isStepGroup: false,
      deleted: false,
      salesforceConnection: `${encodeCustomSpecialCharacters(
        selectedConnection?.name,
      )}`,
    };
    // @ts-ignore: suppressed to remove warning for boolean values in queryParams
    const query = `,${getFormattedQuery(queryParams)}`;

    const paramObj = {};
    paramObj['query'] = query;
    paramObj['size'] = `${20}`;
    return paramObj;
  }, [
    selectedConnection?.name,
    userPreferenceContext?.userPreference?.preference?.projectPreference
      .versionId,
  ]);

  const stepGroupParams = useMemo(() => {
    const versionId =
      userPreferenceContext?.userPreference?.preference?.projectPreference
        .versionId;

    const queryParams = {
      applicationVersionId: `${versionId}`,
      afterTestParentId: null,
      deleted: false,
      isStepGroup: true,
      salesforceConnection: `${encodeCustomSpecialCharacters(
        selectedConnection?.name,
      )}`,
    };

    const query = `,${getFormattedQuery(queryParams)}`;

    const paramObj = {};
    paramObj['query'] = query;
    paramObj['size'] = `${20}`;
    return paramObj;
  }, [
    selectedConnection?.name,
    userPreferenceContext?.userPreference?.preference?.projectPreference
      .versionId,
  ]);

  // fetch linked test cases
  const { data: linkedTestCases, refreshListData: refreshTestCases } =
    useFetchListData<TestCase>(
      TestCaseService.findAll,
      testCaseParams,
      null,
      false,
    );

  // fetch linked step groups
  const { data: linkedStepGroups, refreshListData: refreshStepGroups } =
    useFetchListData<TestCase>(
      TestCaseService.findAll,
      stepGroupParams,
      null,
      false,
    );

  useEffect(() => {
    if (linkedTestCases?.content?.length || linkedStepGroups?.content?.length) {
      handleUpdateScreenMode('linked-entities');
    } else {
      handleUpdateScreenMode('delete');
    }
  }, [linkedTestCases, linkedStepGroups, handleUpdateScreenMode]);

  const reload = () => {
    handleUpdateScreenMode('loading');

    refreshStepGroups();
    refreshTestCases();
  };

  const handleCancel = () => {
    handleHideDeleteModal();
    handleUpdateScreenMode('loading');
  };

  return {
    linkedTestCases,
    linkedStepGroups,
    handleCancel,
    reload,
  };
};

export { useFetchLinkedEntities };
